import { PlannedDurationOptions } from '@carsys/enums/planned-duration-options'
import { PropsWithChildren, createContext, useContext } from 'react'
import { useSettings } from './use-settings'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider } from '@mui/material'
import { useTranslation } from 'react-i18next'

interface Settings {
  PlannedDurationAutoAdjustment: PlannedDurationOptions
}

interface ErrorResponse {
  code: number
}

// `undefined` means "no provider above", `null` means "settings not loaded yet".
const settingsContext = createContext<Settings | null | undefined>(undefined)

export const SettingsContextProvider = ({ children }: PropsWithChildren<{}>) => {
  const { t } = useTranslation()
  const settings = useSettings()
  if (settings.isError) {
    const error = settings.error as ErrorResponse
    const statusCode = error.code
    if (statusCode === 401) {
      return (
        <Dialog open>
          <DialogTitle>{t('Setting.SettingsSessionDialog.Title')}</DialogTitle>
          <Divider />
          <DialogContent>{t('Setting.SettingsSessionDialog.Description')}</DialogContent>
          <Divider />
          <DialogActions style={{ justifyContent: 'flex-end' }}>
            <Button variant='contained' color='primary' onClick={() => settings.refetch()} style={{ textTransform: 'uppercase' }}>
              {t('Setting.SettingsSessionDialog.ButtonText')}
            </Button>
          </DialogActions>
        </Dialog>
      )
    }
    return (
      <Dialog open>
        <DialogTitle>{t('Setting.SettingsErrorDialog.Title')}</DialogTitle>
        <DialogContent>
          {t('Setting.SettingsErrorDialog.Description')}
          <Button onClick={() => settings.refetch()}>{t('Setting.SettingsErrorDialog.ButtonText')}</Button>
        </DialogContent>
      </Dialog>
    )
  }

  return <settingsContext.Provider value={settings.data ?? null}>{children}</settingsContext.Provider>
}

export const useSettingsContext = () => {
  const value = useContext(settingsContext)

  if (value === undefined) throw new Error('useSettingsContext must be used within a SettingsContextProvider')
  return value
}
